import { useEffect, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'

/**
 * DateScrubber — horizontal scrollable pill row of dates.
 * Today anchored coral, selected day anchored mint, other days have a subtle
 * dot indicator if they have activity.
 *
 * Auto-scrolls to keep the selected date visible.
 */

interface Props {
  /** ISO date string (YYYY-MM-DD) of the currently selected day, or null for "all" */
  selectedDate: string | null
  /** Called with an ISO date string (YYYY-MM-DD), or null to clear filter */
  onSelect: (date: string | null) => void
  /** Map of ISO date -> event count for that day (subset — only recent days) */
  activityByDay: Map<string, number>
  /** How many past days to render as pills. Default 30. */
  daysBack?: number
}

export function DateScrubber({ selectedDate, onSelect, activityByDay, daysBack = 30 }: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const selectedPillRef = useRef<HTMLButtonElement>(null)

  const days = useMemo(() => {
    // Newest first (right-to-left reading feels backwards on scroll; keep oldest-left,
    // today-right so scrolling right = today, scrolling left = past. Matches the way
    // Apple Health / Nara Baby scrubbers work.)
    const arr: Array<{ iso: string; d: Date }> = []
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    for (let i = daysBack - 1; i >= 0; i--) {
      const d = new Date(now)
      d.setDate(d.getDate() - i)
      arr.push({ iso: toIsoDate(d), d })
    }
    return arr
  }, [daysBack])

  const todayIso = toIsoDate(new Date())

  // Scroll to selected day (or today if none selected) on mount + when selection changes
  useEffect(() => {
    if (!scrollerRef.current) return
    const target = selectedPillRef.current
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    } else {
      // No selection — scroll all the way right to today
      scrollerRef.current.scrollTo({ left: scrollerRef.current.scrollWidth, behavior: 'smooth' })
    }
  }, [selectedDate])

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-2 snap-x snap-mandatory"
      >
        {days.map(({ iso, d }) => {
          const isToday = iso === todayIso
          const isSelected = iso === selectedDate
          const count = activityByDay.get(iso) ?? 0
          return (
            <button
              key={iso}
              ref={isSelected ? selectedPillRef : undefined}
              type="button"
              onClick={() => onSelect(isSelected ? null : iso)}
              className={`snap-center relative shrink-0 flex flex-col items-center justify-center rounded-r-md px-3 py-2 min-w-[52px] transition-all duration-200 ${
                isSelected
                  ? 'text-white'
                  : isToday
                    ? 'text-white'
                    : 'text-text-secondary'
              }`}
              style={{
                background: isSelected
                  ? 'linear-gradient(180deg, #40DDAF 0%, #2ED2A3 55%, #22B98D 100%)'
                  : isToday
                    ? 'linear-gradient(180deg, #FF8942 0%, #FF7A2D 55%, #F26A1F 100%)'
                    : 'rgb(var(--bg-elevated) / 0.85)',
                boxShadow: isSelected
                  ? '0 8px 20px -6px rgba(46,210,163,0.45)'
                  : isToday
                    ? '0 8px 20px -6px rgba(255,122,45,0.45)'
                    : 'inset 0 0 0 1px rgb(var(--divider-subtle) / 0.08)',
              }}
              aria-label={`${isToday ? 'Today' : d.toDateString()}${count ? `, ${count} events` : ''}`}
              aria-pressed={isSelected}
            >
              <span className="text-tag opacity-70">
                {d.toLocaleDateString([], { weekday: 'short' }).toUpperCase().slice(0, 3)}
              </span>
              <span className="text-title font-display font-medium leading-none mt-0.5">
                {d.getDate()}
              </span>
              {count > 0 && !isSelected && !isToday && (
                <span
                  className="absolute bottom-1 h-1.5 w-1.5 rounded-full"
                  style={{ background: '#FF7A2D', boxShadow: '0 0 6px rgba(255,122,45,0.55)' }}
                />
              )}
              {count > 0 && (isSelected || isToday) && (
                <span className="text-tag opacity-90 mt-1 leading-none">{count}</span>
              )}
            </button>
          )
        })}
      </div>
      {selectedDate && (
        <motion.button
          initial={{ opacity: 0, x: 4 }}
          animate={{ opacity: 1, x: 0 }}
          type="button"
          onClick={() => onSelect(null)}
          className="absolute right-4 -top-8 text-caption text-text-tertiary underline underline-offset-2 hover:text-text-primary"
        >
          Clear filter
        </motion.button>
      )}
    </div>
  )
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}
